import { formatConductorStage } from './conductorUtils'

import type { ConductorGateVerdict, ConductorInstance } from './conductor.types'

export const CADENCE_PLAYBOOK_KEY = 'billing-cadence-triage'
export const CONDUCTOR_ACCOUNT_PAGE_SIZE = 50

const OPEN_INSTANCE_STATUSES = new Set(['ACTIVE', 'PAUSED'])
const LIVE_INSTANCE_STATUSES = new Set(['ACTIVE', 'PAUSED', 'BLOCKED'])

export function openWorkItemCount(instances: readonly ConductorInstance[]): number {
  return instances
    .filter((instance) => OPEN_INSTANCE_STATUSES.has(instance.status))
    .reduce((sum, instance) => sum + instance.openWorkItems, 0)
}

/** True when a cadence playbook is still in flight (not COMPLETED). */
export function hasCadenceInstance(instances: readonly ConductorInstance[]): boolean {
  return instances.some(
    (instance) => instance.playbookKey === CADENCE_PLAYBOOK_KEY && LIVE_INSTANCE_STATUSES.has(instance.status)
  )
}

/** Worst-first header label: Blocked, then failed outbox, then cadence or open count. Null when none apply. */
export function conductorAccountChipLabel(
  instances: readonly ConductorInstance[],
  opts?: { failedOutbox?: boolean }
): string | null {
  if (instances.some((instance) => instance.status === 'BLOCKED')) {
    return 'Blocked'
  }
  if (opts?.failedOutbox === true) {
    return 'Failed action'
  }
  if (instances.length === 0) {
    return null
  }
  const cadence = instances.find(
    (instance) => instance.playbookKey === CADENCE_PLAYBOOK_KEY && instance.status === 'ACTIVE'
  )
  if (cadence != null) {
    return `Cadence · ${formatConductorStage(cadence.currentStage)}`
  }
  const open = openWorkItemCount(instances)
  if (open > 0) {
    return `${open} open`
  }
  return null
}

export function conductorTabBadge(instances: readonly ConductorInstance[]): number | string | undefined {
  if (instances.some((instance) => instance.status === 'BLOCKED')) {
    return 'Blocked'
  }
  const open = openWorkItemCount(instances)
  return open > 0 ? open : undefined
}

export function sortVerdictsFailFirst(verdicts: readonly ConductorGateVerdict[]): ConductorGateVerdict[] {
  const rank = (verdict: string): number => {
    if (verdict === 'FAIL') return 0
    if (verdict === 'UNKNOWN') return 1
    return 2
  }
  return [...verdicts].sort((a, b) => {
    const byVerdict = rank(a.verdict) - rank(b.verdict)
    if (byVerdict !== 0) return byVerdict
    return a.gateId.localeCompare(b.gateId)
  })
}
